import {equals, isNil, omit, pick} from 'ramda';

import {
  type PatchUpsellRequestBody,
  type UpsellPriceGroup,
  type UpsellRequestBody,
} from '@dms/api/sales';

const PRICE_GROUPS = {
  purchasePrice: {
    amounts: ['purchasePrice', 'purchasePriceWithVat'],
    vat: ['purchaseVatRate', 'purchaseVatCode'],
  },
  catalogPrice: {
    amounts: ['catalogPrice', 'catalogPriceWithVat'],
    vat: ['catalogVatRate', 'catalogVatCode'],
  },
  sellingPrice: {
    amounts: ['sellingPrice', 'sellingPriceWithVat'],
    vat: ['sellingVatRate', 'sellingVatCode'],
  },
} as const satisfies Record<
  UpsellPriceGroup,
  {amounts: (keyof UpsellRequestBody)[]; vat: (keyof UpsellRequestBody)[]}
>;

interface UpsellPatchBodyParams {
  body: UpsellRequestBody;
  initialBody: UpsellRequestBody;
  hiddenGroups: UpsellPriceGroup[];
}

/**
 * Keeps only the price groups the user changed. A hidden or unchanged group is omitted, so the
 * server keeps the stored price. A group whose amounts were emptied is sent all-null, which
 * clears it; a non-null VAT key there would read as a VAT-only edit.
 */
export const getUpsellPatchBody = (params: UpsellPatchBodyParams): PatchUpsellRequestBody =>
  Object.entries(PRICE_GROUPS).reduce<PatchUpsellRequestBody>((patchBody, [group, keys]) => {
    const groupKeys = [...keys.amounts, ...keys.vat];
    const isHidden = params.hiddenGroups.some(equals(group));
    const isUnchanged = equals(pick(groupKeys, params.body), pick(groupKeys, params.initialBody));

    if (isHidden || isUnchanged) {
      return omit(groupKeys, patchBody);
    }

    const isEmptied = keys.amounts.every((key) => isNil(params.body[key]));

    return isEmptied ? {...patchBody, ...nullValues(keys.vat)} : patchBody;
  }, params.body);

const nullValues = (keys: readonly (keyof UpsellRequestBody)[]) =>
  Object.fromEntries(keys.map((key) => [key, null]));
